import React from "react";
import {
  Box,
  Chip,
  Dropdown,
  ListDivider,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
} from "@mui/joy";
import {
  SortIcon,
  SortByAlphaIcon,
  NumbersIcon,
  EventIcon,
  ArrowUpwardIcon,
  ArrowDownwardIcon,
} from "../../../styles/StyledMUI";
import type {
  EquipmentAttributeDefinition,
  EquipmentModel,
} from "../../../api/equipment/Equipment.typed";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";

export interface EquipmentsSortControlsProps {
  selectedModel: EquipmentModel | undefined;
  sortBy: string | undefined;
  effectiveSortOrder: "ASC" | "DESC";
  sortableAttributes: EquipmentAttributeDefinition[];
  currentSortAttr: EquipmentAttributeDefinition | undefined;
  onPickSort: (attrId?: string, order?: "ASC" | "DESC") => void;
  onToggleSortOrder: () => void;
}

const sortMenuListboxSx = {
  zIndex: 10000,
  backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "10px",
  minWidth: 240,
  maxHeight: { xs: "45vh", sm: "55vh" },
  overflowY: "auto",
  py: 0.5,
  "& .MuiMenuItem-root": {
    color: `${colorTokens.text.primary} !important`,
    fontSize: { xs: "0.8rem", sm: "0.875rem" },
    minHeight: { xs: 32, sm: 36 },
    "&:hover": {
      backgroundColor: `${colorTokens.bg.hover} !important`,
    },
    "&.Mui-selected": {
      backgroundColor: `${colorTokens.accent.primary} !important`,
      color: `${colorTokens.accent.contrastText} !important`,
      "&:hover": {
        backgroundColor: `${colorTokens.accent.hover} !important`,
      },
    },
  },
};

const sortMenuHeaderSx = {
  px: 1.5,
  py: 0.5,
  color: colorTokens.text.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
};

function attrIcon(type: EquipmentAttributeDefinition["Type"] | undefined) {
  if (type === "string") return <SortByAlphaIcon sx={{ fontSize: 18 }} />;
  if (type === "number") return <NumbersIcon sx={{ fontSize: 18 }} />;
  if (type === "date") return <EventIcon sx={{ fontSize: 18 }} />;

  return null;
}

function EquipmentsSortControlsInner({
  selectedModel,
  sortBy,
  effectiveSortOrder,
  sortableAttributes,
  currentSortAttr,
  onPickSort,
  onToggleSortOrder,
}: EquipmentsSortControlsProps) {
  const [open, setOpen] = React.useState(false);

  if (!selectedModel) return null;

  const stringAttrs = sortableAttributes.filter((a) => a.Type === "string");
  const numberAttrs = sortableAttributes.filter((a) => a.Type === "number");
  const dateAttrs = sortableAttributes.filter((a) => a.Type === "date");

  const pickCreated = (order: "ASC" | "DESC") => {
    onPickSort(undefined, order);
    setOpen(false);
  };

  const pickAttr = (attrId: string) => {
    onPickSort(attrId);
    setOpen(false);
  };

  const selectedCreatedRecent = !sortBy && effectiveSortOrder === "DESC";
  const selectedCreatedOld = !sortBy && effectiveSortOrder === "ASC";

  const groupLabel = (icon: React.ReactNode, label: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        ...sortMenuHeaderSx,
      }}
    >
      {icon}
      {label}
    </Box>
  );

  const renderAttrItem = (a: EquipmentAttributeDefinition) => (
    <MenuItem
      key={a.ID}
      selected={sortBy === a.ID}
      onClick={(e) => {
        e.preventDefault();
        pickAttr(a.ID);
      }}
    >
      <ListItemDecorator sx={{ color: colorTokens.text.muted }}>
        {attrIcon(a.Type)}
      </ListItemDecorator>
      {a.Label}
    </MenuItem>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Dropdown open={open} onOpenChange={(_, isOpen) => setOpen(!!isOpen)}>
        <Tooltip title="Trier par">
          <MenuButton
            slots={{ root: IconButtonCTA }}
            slotProps={{
              root: { size: "sm", "aria-label": "Choisir le tri", ctaVariant: "accent" },
            }}
            sx={{ border: "none" }}
          >
            <SortIcon />
          </MenuButton>
        </Tooltip>
        <Menu placement="bottom-start" sx={sortMenuListboxSx}>
          <MenuItem
            selected={selectedCreatedRecent}
            onClick={(e) => {
              e.preventDefault();
              pickCreated("DESC");
            }}
          >
            <ListItemDecorator sx={{ color: colorTokens.text.muted }}>
              <EventIcon sx={{ fontSize: 18 }} />
            </ListItemDecorator>
            Date de création • récent
          </MenuItem>
          <MenuItem
            selected={selectedCreatedOld}
            onClick={(e) => {
              e.preventDefault();
              pickCreated("ASC");
            }}
          >
            <ListItemDecorator sx={{ color: colorTokens.text.muted }}>
              <EventIcon sx={{ fontSize: 18 }} />
            </ListItemDecorator>
            Date de création • ancien
          </MenuItem>

          {sortableAttributes.length === 0 ? null : <ListDivider />}

          {stringAttrs.length > 0 && (
            <>
              {groupLabel(<SortByAlphaIcon sx={{ fontSize: 14 }} />, "Texte")}
              {stringAttrs.map(renderAttrItem)}
            </>
          )}
          {numberAttrs.length > 0 && (
            <>
              {stringAttrs.length > 0 && <ListDivider />}
              {groupLabel(<NumbersIcon sx={{ fontSize: 14 }} />, "Nombre")}
              {numberAttrs.map(renderAttrItem)}
            </>
          )}
          {dateAttrs.length > 0 && (
            <>
              {(stringAttrs.length > 0 || numberAttrs.length > 0) && <ListDivider />}
              {groupLabel(<EventIcon sx={{ fontSize: 14 }} />, "Date")}
              {dateAttrs.map(renderAttrItem)}
            </>
          )}
        </Menu>
      </Dropdown>

      {currentSortAttr?.Label && (
        <Chip
          size="sm"
          variant="soft"
          sx={{
            bgcolor: withAlpha(colorTokens.accent.primary, 0.12),
            color: colorTokens.accent.primary,
            border: `1px solid ${withAlpha(colorTokens.accent.primary, 0.35)}`,
            fontWeight: 600,
          }}
        >
          {currentSortAttr.Label}
        </Chip>
      )}

      {sortBy && (
        <Tooltip
          title={
            currentSortAttr?.Type === "string"
              ? effectiveSortOrder === "ASC"
                ? "A → Z"
                : "Z → A"
              : effectiveSortOrder === "ASC"
                ? "Croissant"
                : "Décroissant"
          }
        >
          <span>
            <IconButtonCTA
              ctaVariant="neutral"
              size="sm"
              aria-label="Inverser l'ordre de tri"
              onClick={onToggleSortOrder}
            >
              {effectiveSortOrder === "ASC" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </IconButtonCTA>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

export default React.memo(EquipmentsSortControlsInner);
